import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import type { FeaturedPartnerV2 } from '@rd/types';
import {
  EmblaCarouselDirective,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel-angular';
import { BnzFeaturedPartnerV2BannerComponent } from './featured-partner-v2-banner.component';

@Component({
  selector: 'bnz-featured-partner-v2-carousel',
  template: `
    <div class="flex flex-col gap-6 overflow-x-clip lg:overflow-x-visible -mx-4 px-4">
      <div class="flex items-center justify-between gap-4">
        <h2
          *ngIf="heading"
          class="typo-heading-2-desktop m-0 mb-2 lg:mb-0 text-[#002F6B] lg:[--heading-line-height:40px]"
          style="line-height: var(--heading-line-height, 30px);"
        >
          {{ heading }}
        </h2>
        <div class="lg:flex shrink-0 items-center gap-3 ml-auto hidden">
          <button
            type="button"
            class="button-reset focus-link flex h-6 w-6 items-center justify-center disabled:opacity-30"
            aria-label="Previous featured partner"
            [disabled]="isPrevDisabled()"
            (click)="onPrev()">
            <rd-shared-icon
              [iconSymbol]="'icon-chevron-left'"
            />
          </button>

          <span class="typo-text-emphasis-1-mobile lg:typo-emphasis-2-mobile tabular-nums text-[#333333]">
            {{ currentIndex() }}/{{ banners.length }}
          </span>

          <button
            type="button"
            class="button-reset focus-link flex h-6 w-6 items-center justify-center disabled:opacity-30"
            aria-label="Next featured partner"
            [disabled]="isNextDisabled()"
            (click)="onNext()">
            <rd-shared-icon
              [iconSymbol]="'icon-chevron-right'"
            />
          </button>
        </div>
      </div>

      <div
        class="featured-partner-carousel-viewport overflow-visible"
        emblaCarousel
        [options]="emblaOptions"
        [subscribeToEvents]="subscribeToEvents"
        (emblaChange)="onEmblaChange()">
        <div class="flex w-full gap-6 lg:gap-8">
          <bnz-featured-partner-v2-banner
            *ngFor="let banner of banners; index as i; trackBy: trackByBanner"
            [banner]="banner"
            [id]="i"
            class="shrink-0 w-full max-w-[calc(100%-24px)] lg:max-w-full"
          >
          </bnz-featured-partner-v2-banner>
        </div>
      </div>

      <div class="shrink-0 items-center gap-3 flex lg:hidden mt-1">
        <button
          type="button"
          class="button-reset focus-link flex h-6 w-6 items-center justify-center disabled:opacity-30"
          aria-label="Previous featured partner"
          [disabled]="isPrevDisabled()"
          (click)="onPrev()">
          <rd-shared-icon
            [iconSymbol]="'icon-chevron-left'"
          />
        </button>

        <span class="typo-text-emphasis-1-mobile lg:typo-emphasis-2-mobile tabular-nums text-[#333333]">
          {{ currentIndex() }}/{{ banners.length }}
        </span>

        <button
          type="button"
          class="button-reset focus-link flex h-6 w-6 items-center justify-center disabled:opacity-30"
          aria-label="Next featured partner"
          [disabled]="isNextDisabled()"
          (click)="onNext()">
          <rd-shared-icon
            [iconSymbol]="'icon-chevron-right'"
          />
        </button>
      </div>
    </div>
  `,
  imports: [
    CommonModule,
    TranslateModule,
    IconModule,
    EmblaCarouselDirective,
    BnzFeaturedPartnerV2BannerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BnzFeaturedPartnerV2CarouselComponent {
  @HostBinding('class') hostClass = 'block w-full';

  @ViewChild(EmblaCarouselDirective) emblaRef?: EmblaCarouselDirective;
  @Input() banners: FeaturedPartnerV2[];
  @Input() heading?: string;

  private cdRef = inject(ChangeDetectorRef);

  readonly emblaOptions: EmblaOptionsType = {
    watchDrag: false,
    loop: false,
  };
  readonly subscribeToEvents: EmblaEventType[] = ['select'];

  currentIndex(): number {
    return this.scrollIndex() + 1;
  }

  isPrevDisabled(): boolean {
    return this.scrollIndex() === 0;
  }

  isNextDisabled(): boolean {
    return this.scrollIndex() >= this.banners.length - 1;
  }

  onPrev(): void {
    this.emblaRef?.scrollPrev();
    this.cdRef.markForCheck();
  }

  onNext(): void {
    this.emblaRef?.scrollNext();
    this.cdRef.markForCheck();
  }

  onEmblaChange(): void {
    this.cdRef.markForCheck();
  }

  trackByBanner(_index: number, banner: FeaturedPartnerV2): string {
    return `${banner.title}-${banner.partnerQueryString}`;
  }

  /**
   * Read the live instance on every call. embla-carousel-angular >= 21 creates
   * `emblaApi` in `afterNextRender`, i.e. after this component's
   * `ngAfterViewInit`, so caching it there captured `undefined`.
   */
  private scrollIndex(): number {
    return this.emblaRef?.emblaApi?.selectedScrollSnap() ?? 0;
  }
}
